import { useLayoutEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TabItem } from './navConfig'
import styles from './TabBar.module.css'

export function TabBar({
  items,
  onOpenMore,
}: {
  items: TabItem[]
  onOpenMore: () => void
}) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const bubbleRef = useRef<HTMLSpanElement>(null)

  // Slide the glass bubble under the active tab (spring easing is in the CSS).
  useLayoutEffect(() => {
    const nav = navRef.current
    const bubble = bubbleRef.current
    if (!nav || !bubble) return
    const active = nav.querySelector<HTMLElement>(`.${styles.tabActive}`)
    if (!active) {
      bubble.style.opacity = '0'
      return
    }
    bubble.style.opacity = '1'
    bubble.style.width = `${active.offsetWidth}px`
    bubble.style.transform = `translateX(${active.offsetLeft - 4}px)`
  }, [pathname, items])

  return (
    <nav ref={navRef} className={styles.tabbar} aria-label="Main navigation">
      <span ref={bubbleRef} className={styles.bubble} data-glass-bubble aria-hidden="true" />
      {items.map((item) => {
        const Icon = item.icon
        if (item.opensMore) {
          return (
            <button
              key={t(item.labelKey)}
              type="button"
              className={styles.tab}
              onClick={onOpenMore}
            >
              <span className={styles.icon}><Icon size={20} strokeWidth={1.75} /></span>
              <span className={styles.label}>{t(item.labelKey)}</span>
            </button>
          )
        }
        return (
          <NavLink
            key={t(item.labelKey)}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `${styles.tab}${isActive ? ' ' + styles.tabActive : ''}`
            }
          >
            <span className={styles.icon}><Icon size={20} strokeWidth={1.75} /></span>
            <span className={styles.label}>{t(item.labelKey)}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}
